import { ACTIVITY_TYPES } from './activity_types'
import { qk } from '../hooks/queryKeys'

/**
 * Build the payload representing a delete (clear) operation for an answer of a given activity.
 *
 * The structure of the `answerToDelete` field varies by activity `type` so that downstream
 * mutation handlers / API calls can distinguish what to remove. In all cases, an `id` (if found)
 * and the index of the located answer within its collection (`indexAGA`) are returned to allow
 * reducer logic to optimistically update local state quickly.
 *
 * Activity type variants:
 * 1. ACTIVITY_TYPES.SELECT_SINGLE
 *    - Searches `activityGroupAnswers.selections` for a matching `activityId`.
 *    - Returns `{ answerToDelete: { type: 'selection', selections: [ { id, optionId?, customResponse?, activityId, delete: true } ] }, id, indexAGA }`.
 *      (Note: `optionId` is only sent if > 0; `customResponse` may be undefined.)
 *
 * 2. ACTIVITY_TYPES.RANKING
 *    - Looks in `activityGroupAnswers.rankings` (or legacy `ranking`).
 *    - Returns `{ answerToDelete: { type: 'ranking', id, activityId, delete: true }, id, indexAGA }`.
 *
 * 3. ACTIVITY_TYPES.QUESTION_ANSWER / ACTIVITY_TYPES.QUESTION_ANSWER_PHOTOS
 *    - Looks in `activityGroupAnswers.conversations`.
 *    - Returns `{ answerToDelete: { type, id, activityId, delete: true }, id, indexAGA }`.
 *
 * 4. Any other / unfound
 *    - Returns `{ answerToDelete: null, id: null, indexAGA: -1 }`.
 *
 * @param {Object} params
 * @param {number|string} params.activityId - ID of the activity whose answer should be cleared.
 * @param {string} params.type - One of ACTIVITY_TYPES indicating how to interpret existing answers.
 * @param {Object} [params.activityGroupAnswers] - Container holding existing grouped answers. Shape may include
 *   `selections`, `rankings`/`ranking`, or `conversations` arrays depending on activities previously answered.
 *
 * @returns {Object} Result
 * @returns {Object|null} returns.answerToDelete - Payload fragment describing the delete operation expected by the API / reducer.
 * @returns {string|number|null} returns.id - The id of the specific answer entry (if found) used for direct removal.
 * @returns {number} returns.indexAGA - Zero-based index within its array collection; -1 if not found.
 *
 * @example
 * // Single selection removal
 * buildDeletePayload({ activityId: 42, type: ACTIVITY_TYPES.SELECT_SINGLE, activityGroupAnswers })
 * // => { answerToDelete: { type: 'selection', selections: [ { id: 7, optionId: 3, customResponse: 'Blue', activityId: 42, delete: true } ] }, id: 7, indexAGA: 0 }
 *
 * @example
 * // Ranking removal
 * buildDeletePayload({ activityId: 5, type: ACTIVITY_TYPES.RANKING, activityGroupAnswers })
 * // => { answerToDelete: { type: 'ranking', id: 11, activityId: 5, delete: true }, id: 11, indexAGA: 2 }
 */
export const buildDeletePayload = ({ activityId, type, activityGroupAnswers }) => {
  if (type === ACTIVITY_TYPES.SELECT_SINGLE) {
    const selections = activityGroupAnswers?.selections || []
    const selection = selections.find((x) => x.activityId === activityId)
    const { optionId, customResponse, id } = selection || {}
    return {
      answerToDelete: {
        type: 'selection',
        selections: [{ id, optionId: optionId > 0 ? optionId : undefined, customResponse, activityId, delete: true }],
      },
      id,
      indexAGA: selections.findIndex((x) => x.activityId === activityId),
    }
  }
  if (type === ACTIVITY_TYPES.RANKING) {
    const rankings = activityGroupAnswers?.ranking || activityGroupAnswers?.rankings || []
    const rankingEntry = rankings.find((x) => x.activityId === activityId)
    return {
      answerToDelete: { type, id: rankingEntry?.id, activityId, delete: true },
      id: rankingEntry?.id,
      indexAGA: rankings.findIndex((x) => x.activityId === activityId),
    }
  }
  if (type === ACTIVITY_TYPES.QUESTION_ANSWER || type === ACTIVITY_TYPES.QUESTION_ANSWER_PHOTOS) {
    const conversations = activityGroupAnswers?.conversations || []
    const convo = conversations.find((x) => x.activityId === activityId)
    return {
      answerToDelete: { type, id: convo?.id, activityId, delete: true },
      id: convo?.id,
      indexAGA: conversations.findIndex((x) => x.activityId === activityId),
    }
  }
  return { answerToDelete: null, id: null, indexAGA: -1 }
}

// Apply optimistic local cache update for selection removal when answers not in local state
export const applyLocalOptimisticClear = ({
  type,
  activityId,
  activityGroupAnswerId,
  stateActivityGroupAnswers,
  cisId,
  requestedAnswerOwnerCisId,
  requestedActivityGroupId,
  activeCisId,
  relevantActivityGroupId,
  queryClient,
}) => {
  if (type !== ACTIVITY_TYPES.SELECT_SINGLE || stateActivityGroupAnswers || !activityGroupAnswerId) return
  const cisUserIdForKey = cisId ?? requestedAnswerOwnerCisId ?? activeCisId
  const key = qk.answerById({
    activityGroupId: relevantActivityGroupId ?? requestedActivityGroupId,
    activityGroupAnswerId,
    cisUserId: cisUserIdForKey,
  })
  const prev = queryClient.getQueryData(key)
  if (prev?.data?.selections) {
    const newSelections = prev.data.selections.filter((s) => s.activityId !== activityId)
    queryClient.setQueryData(key, { ...prev, data: { ...prev.data, selections: newSelections } })
  }
}

// --- DRY helpers for reducer CLEAR_ANSWER logic ---

// Remove answer structures from activityGroupAnswers based on type
export const removeAnswerFromActivityGroupAnswers = ({ activityGroupAnswers, type, activityId, indexAGA }) => {
  if (!activityGroupAnswers) return null
  if (type === ACTIVITY_TYPES.SELECT_SINGLE) {
    const current = activityGroupAnswers?.selections || []
    const newSelections =
      indexAGA > -1
        ? [...current.slice(0, indexAGA), ...current.slice(indexAGA + 1)]
        : current.filter((s) => s.activityId !== activityId)
    return { ...activityGroupAnswers, selections: newSelections }
  }
  if (type === ACTIVITY_TYPES.RANKING) {
    const rankingArr = activityGroupAnswers?.rankings || activityGroupAnswers?.ranking || []
    if (!rankingArr?.length) {
      if (activityGroupAnswers?.ranking) {
        const { ranking, ...rest } = activityGroupAnswers
        return rest
      }
      return activityGroupAnswers
    }
    const newRankings =
      indexAGA > -1
        ? [...rankingArr.slice(0, indexAGA), ...rankingArr.slice(indexAGA + 1)]
        : rankingArr.filter((r) => r.activityId !== activityId)
    const { ranking, ...rest } = activityGroupAnswers // drop legacy key
    return { ...rest, rankings: newRankings }
  }
  if (type === ACTIVITY_TYPES.QUESTION_ANSWER || type === ACTIVITY_TYPES.QUESTION_ANSWER_PHOTOS) {
    const conversations = activityGroupAnswers?.conversations || []
    const newConversations =
      indexAGA > -1
        ? [...conversations.slice(0, indexAGA), ...conversations.slice(indexAGA + 1)]
        : conversations.filter((c) => c.activityId !== activityId)
    return { ...activityGroupAnswers, conversations: newConversations }
  }
  return activityGroupAnswers
}

// Remove activity from selectedActivitiesWithAnswers collection
export const removeFromSelectedActivities = (selectedActivitiesWithAnswers, activityId) => {
  if (!selectedActivitiesWithAnswers?.length) return selectedActivitiesWithAnswers
  const idx = selectedActivitiesWithAnswers.findIndex((a) => a?.activityId === activityId)
  if (idx === -1) return selectedActivitiesWithAnswers
  return [...selectedActivitiesWithAnswers.slice(0, idx), ...selectedActivitiesWithAnswers.slice(idx + 1)]
}
